import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Brand, BrandsPaginatedResponse, OnboardBrandForm } from "../../types/pages/onboarding";

export interface FetchAllBrandsParams {
    page: number;
    rowsPerPage: number;
    sortBy?: string;
    sortOrder?: number;
}

interface BrandsState {
    brands: Brand[];
    loading: boolean;
    error: string | null;
    totalCount: number;
    page: number;
    rowsPerPage: number;
    sortBy: string;
    sortOrder: number;
    selectedBrand: Brand | null;
    fetchBrandLoading: boolean;
    fetchBrandError: string | null;
    onboarding: boolean;
    onboardSuccess: boolean;
    onboardError: string | null;
}

const initialState: BrandsState = {
    brands: [],
    loading: false,
    error: null,
    totalCount: 0,
    page: 0,
    rowsPerPage: 10,
    sortBy: "created_at",
    sortOrder: -1,
    selectedBrand: null,
    fetchBrandLoading: false,
    fetchBrandError: null,
    onboarding: false,
    onboardSuccess: false,
    onboardError: null,
};

const brandsSlice = createSlice({
    name: "brands",
    initialState,
    reducers: {
        fetchAllBrands: (state, _action: PayloadAction<FetchAllBrandsParams>) => { },
        fetchAllBrandsStart: (state) => {
            state.loading = true;
            state.error = null;
        },
        fetchAllBrandsSuccess: (state, action: PayloadAction<BrandsPaginatedResponse>) => {
            state.loading = false;
            state.brands = action.payload.data;
            state.totalCount = action.payload.total;
        },
        fetchAllBrandsFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        setRowsPerPage: (state, action: PayloadAction<number>) => {
            state.rowsPerPage = action.payload;
            state.page = 0;
        },
        setSortBy: (state, action: PayloadAction<string>) => {
            state.sortBy = action.payload;
            state.page = 0;
        },
        setSortOrder: (state, action: PayloadAction<number>) => {
            state.sortOrder = action.payload;
            state.page = 0;
        },

        fetchBrandById: (state, _action: PayloadAction<string>) => { },
        fetchBrandByIdStart: (state) => {
            state.fetchBrandLoading = true;
            state.fetchBrandError = null;
            state.selectedBrand = null;
        },
        fetchBrandByIdSuccess: (state, action: PayloadAction<Brand>) => {
            state.fetchBrandLoading = false;
            state.selectedBrand = action.payload;
        },
        fetchBrandByIdFailure: (state, action: PayloadAction<string>) => {
            state.fetchBrandLoading = false;
            state.fetchBrandError = action.payload;
        },
        clearSelectedBrand: (state) => {
            state.selectedBrand = null;
            state.fetchBrandError = null;
        },

        onboardBrand: (state, _action: PayloadAction<OnboardBrandForm>) => { },
        onboardBrandStart: (state) => {
            state.onboarding = true;
            state.onboardSuccess = false;
            state.onboardError = null;
        },
        onboardBrandSuccess: (state) => {
            state.onboarding = false;
            state.onboardSuccess = true;
        },
        onboardBrandFailure: (state, action: PayloadAction<string>) => {
            state.onboarding = false;
            state.onboardError = action.payload;
        },
        resetOnboardState: (state) => {
            state.onboarding = false;
            state.onboardSuccess = false;
            state.onboardError = null;
        },
    },
});

export const {
    fetchAllBrands,
    fetchAllBrandsStart,
    fetchAllBrandsSuccess,
    fetchAllBrandsFailure,
    setPage,
    setRowsPerPage,
    setSortBy,
    setSortOrder,
    fetchBrandById,
    fetchBrandByIdStart,
    fetchBrandByIdSuccess,
    fetchBrandByIdFailure,
    clearSelectedBrand,
    onboardBrand,
    onboardBrandStart,
    onboardBrandSuccess,
    onboardBrandFailure,
    resetOnboardState,
} = brandsSlice.actions;

export default brandsSlice.reducer;
